"use client";

import { FormEvent, useEffect, useRef, useState } from "react";
import ConsultationPrep from "./ConsultationPrep";
import "./styles.css";

type SubmissionState = "idle" | "sending" | "success" | "error";

const firstSteps = [
  {
    number: "01",
    title: "Locate the original will",
    body: "If one exists, keep the original safe. A copy is helpful, but the original may be needed by the court.",
  },
  {
    number: "02",
    title: "Order death certificates",
    body: "Certified copies are often requested by courts, banks, insurers, and other institutions.",
  },
  {
    number: "03",
    title: "Secure property and records",
    body: "Protect homes, vehicles, mail, financial statements, valuables, and active business records. Keep careful notes.",
  },
  {
    number: "04",
    title: "Pause before distributing",
    body: "Do not assume that property can be divided or debts paid immediately. The correct order can matter.",
  },
];

const paths = [
  {
    label: "Everything has a beneficiary",
    title: "A court estate may not be needed.",
    body: "Assets held in a trust, jointly with survivorship, or with valid beneficiary, transfer-on-death, or payable-on-death designations often pass outside probate. We still verify each asset before reaching that conclusion.",
  },
  {
    label: "The estate appears modest",
    title: "A simpler Ohio procedure may fit.",
    body: "Ohio has summary-release and release-from-administration procedures for qualifying estates. The right path depends on asset values, funeral expenses, the applicant, and whether a surviving spouse qualifies—not simply whether the estate feels straightforward.",
  },
  {
    label: "There is a will or property",
    title: "Full estate administration may be appropriate.",
    body: "When formal authority is needed, the court can admit a will, appoint an executor or administrator, supervise notices and inventory, and approve the final distribution of property.",
  },
  {
    label: "There is conflict or uncertainty",
    title: "Start with risk, not paperwork.",
    body: "A missing will, family dispute, unknown heir, property at risk, business operation, creditor pressure, or out-of-state asset can change the first move. These matters benefit from early legal review.",
  },
];

const faq = [
  ["Does every estate go through probate?", "No. Some assets pass outside probate because of a trust, survivorship ownership, or a beneficiary designation. Other estates may qualify for a simplified court procedure. We identify how each asset is titled before recommending a path."],
  ["What if there is no will?", "Ohio law provides a process for an estate without a will. The court may appoint an administrator, and Ohio's inheritance rules determine who receives probate property. The family information gathered at the beginning becomes especially important."],
  ["How quickly should we call?", "You do not have to solve everything immediately, but early advice can prevent avoidable mistakes. Creditor, notice, inventory, spouse-rights, and accounting dates can apply. Call promptly if property is at risk, a business must keep operating, a court paper has arrived, or family conflict is growing."],
  ["Can the estate pay legal fees?", "In many administrations, reasonable legal fees are paid from estate funds, subject to the applicable court rules and approval process. We discuss scope, fees, and who is responsible before representation begins."],
];

function Arrow() {
  return <span aria-hidden="true">↗</span>;
}

export default function Home() {
  const [activePath, setActivePath] = useState(0);
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [submission, setSubmission] = useState<SubmissionState>("idle");
  const [formMessage, setFormMessage] = useState("");
  const startedAt = useRef(0);

  useEffect(() => {
    startedAt.current = Date.now();
  }, []);

  async function submitIntake(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    setSubmission("sending");
    setFormMessage("");

    const data = Object.fromEntries(new FormData(form).entries());
    const payload = {
      ...data,
      consent: data.consent === "on",
      startedAt: startedAt.current,
    };

    try {
      const response = await fetch("/api/probate-intake", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = (await response.json().catch(() => null)) as { message?: string } | null;
      if (!response.ok) throw new Error(result?.message || "We could not send your request. Please try again.");
      form.reset();
      setSubmission("success");
      setFormMessage("Your information was sent to EQUES Law Group. A member of our intake team will review it and follow up using your preferred contact method.");
    } catch (error) {
      setSubmission("error");
      setFormMessage(error instanceof Error ? error.message : "We could not send your request. Please try again.");
    }
  }

  return (
    <main id="top">
      <header className="site-header">
        <a className="brand" href="#top" aria-label="EQUES Law Group probate home">
          <strong>EQUES</strong><small>LAW GROUP</small>
        </a>
        <p className="header-location">NEWARK-BASED <span>•</span> SERVING FAMILIES ACROSS OHIO</p>
        <a className="header-cta" href="#contact">Ask for guidance <Arrow /></a>
      </header>

      <section className="hero" aria-labelledby="hero-title">
        <div className="hero-copy">
          <p className="eyebrow">OHIO PROBATE · ESTATE ADMINISTRATION</p>
          <h1 id="hero-title">You do not have to know what comes next.</h1>
          <p className="hero-emphasis">You just need a clear first step.</p>
          <p className="hero-lede">After a loved one dies, even ordinary decisions can feel heavy. Based in Newark and serving families through EQUES offices across Ohio, our probate team helps you understand what needs attention now, what can wait, and which Ohio process fits the estate.</p>
          <div className="hero-actions">
            <a className="button button-primary" href="#contact">Tell us about your family <Arrow /></a>
            <a className="text-link" href="#first-steps">What should I do first? <span>↓</span></a>
          </div>
          <div className="trust-line">
            <span>CALM, PLAIN-LANGUAGE GUIDANCE</span>
            <span>NEWARK-BASED PROBATE TEAM</span>
            <span>SERVING EQUES OFFICES ACROSS OHIO</span>
          </div>
        </div>
      </section>

      <section className="reassurance-bar" aria-label="A note for families">
        <p>Grief is not a filing system.</p>
        <span>You can come to us with an incomplete list, unanswered questions, and a box of papers. We will help create order.</span>
      </section>

      <section className="first-steps section-shell" id="first-steps" aria-labelledby="first-steps-title">
        <div className="section-heading split-heading">
          <div><p className="section-kicker">THE FIRST FEW DAYS</p><h2 id="first-steps-title">Protect first.<br />Sort second.</h2></div>
          <p>You are not expected to open an estate before the funeral or immediately master Ohio probate law. These practical steps preserve options while your family catches its breath.</p>
        </div>
        <div className="step-grid">
          {firstSteps.map((step) => <article className="step-card" key={step.number}><span>{step.number}</span><h3>{step.title}</h3><p>{step.body}</p></article>)}
        </div>
        <aside className="urgent-note"><strong>Call sooner if:</strong><span>a home is unsecured, a loved one’s home is in foreclosure or a sheriff’s-sale notice has been received, money is being withdrawn, a business must keep operating, there is immediate family conflict, a creditor is pressing, or a court deadline is already known.</span></aside>
      </section>

      <section className="path-section" aria-labelledby="path-title">
        <div className="path-copy">
          <p className="section-kicker light">DOES PROBATE APPLY?</p>
          <h2 id="path-title">The answer usually begins with how the assets are titled.</h2>
          <p>Choose the statement that sounds closest. This is orientation—not a legal conclusion.</p>
          <div className="path-tabs" role="tablist" aria-label="Possible estate paths">
            {paths.map((path, index) => <button key={path.label} role="tab" aria-selected={activePath === index} onClick={() => setActivePath(index)}><span>0{index + 1}</span>{path.label}</button>)}
          </div>
        </div>
        <div className="path-answer" role="tabpanel">
          <span className="path-number">0{activePath + 1}</span>
          <p className="path-label">A POSSIBLE PATH</p>
          <h3>{paths[activePath].title}</h3>
          <p>{paths[activePath].body}</p>
          <a href="#contact">Let us review the facts <Arrow /></a>
        </div>
      </section>

      <section className="process section-shell" aria-labelledby="process-title">
        <div className="section-heading centered-heading"><p className="section-kicker">WHAT WORKING TOGETHER LOOKS LIKE</p><h2 id="process-title">A clear path through an unfamiliar process.</h2></div>
        <ol className="process-list">
          <li><span>01</span><div><h3>Listen and triage</h3><p>We learn who died, who is calling, what documents exist, how property is titled, and whether anything needs immediate protection.</p></div></li>
          <li><span>02</span><div><h3>Choose the right lane</h3><p>An attorney reviews whether the matter calls for no court filing, a smaller-estate procedure, full administration, ancillary administration, or a problem-estate strategy.</p></div></li>
          <li><span>03</span><div><h3>Build and file</h3><p>We gather the death certificate, will, family information, asset values, debts, deeds, titles, and signatures needed for the opening package.</p></div></li>
          <li><span>04</span><div><h3>Keep the estate moving</h3><p>Our team tracks the next action, responsible person, court dates, notices, inventory, claims, transfers, accountings, and the path to closure.</p></div></li>
        </ol>
      </section>

      <section className="team-section" id="team" aria-labelledby="team-title">
        <div className="team-intro">
          <p className="section-kicker light">YOUR OHIO PROBATE TEAM · BASED IN NEWARK</p>
          <h2 id="team-title">Legal judgment and careful follow-through.</h2>
          <p>Probate is both law and logistics. Zacary makes the legal decisions and helps families understand the strategy. Leana keeps documents, communications, and next steps moving with care.</p>
        </div>
        <div className="people-grid">
          <article className="person-card">
            <img src="/zac-colopy.jpg" alt="Attorney Zacary Colopy of EQUES Law Group" width="1365" height="2048" loading="eager" />
            <div><p>ATTORNEY</p><h3>Zacary Colopy</h3><span>Zacary approaches estate and probate-court matters with empathy, care, and a resolution-oriented mindset. A Licking County community advocate, he works from EQUES’s Newark office.</span><a href="https://www.eques.law/attorneys/zac-colopy" target="_blank" rel="noreferrer">Read Zacary’s biography <Arrow /></a></div>
          </article>
          <article className="person-card">
            <img src="/leana-seymour.jpg" alt="Estate Administration Paralegal Leana Seymour of EQUES Law Group" width="1365" height="2048" loading="eager" />
            <div><p>ESTATE ADMINISTRATION PARALEGAL</p><h3>Leana Seymour</h3><span>Leana helps families turn a difficult collection of papers, questions, and responsibilities into an organized matter with a visible next step.</span></div>
          </article>
        </div>
      </section>

      <section className="gather-section section-shell" aria-labelledby="gather-title">
        <div className="section-heading split-heading">
          <div><p className="section-kicker">WHAT TO GATHER</p><h2 id="gather-title">Bring what you have.<br />We will identify the gaps.</h2></div>
          <p>Do not delay calling because something is missing. This list simply makes the first conversation more useful.</p>
        </div>
        <div className="gather-grid">
          <div><span>01</span><h3>Core documents</h3><ul><li>Certified death certificate, if available</li><li>Original will and any codicils</li><li>Trust or beneficiary documents</li><li>Funeral bill or proof of payment</li></ul></div>
          <div><span>02</span><h3>People</h3><ul><li>Surviving spouse and children</li><li>Other heirs and beneficiaries</li><li>Person named as executor</li><li>Current addresses and phone numbers</li></ul></div>
          <div><span>03</span><h3>Property and obligations</h3><ul><li>Bank and investment statements</li><li>Deeds, vehicle titles, mineral interests</li><li>Insurance and retirement information</li><li>Known debts, loans, and creditor letters</li></ul></div>
        </div>
      </section>

      <ConsultationPrep />

      <section className="faq-section section-shell" aria-labelledby="faq-title">
        <div className="faq-intro"><p className="section-kicker">COMMON FIRST QUESTIONS</p><h2 id="faq-title">A little clarity before we talk.</h2><p>Every estate is different. These answers explain the landscape without pretending the details do not matter.</p></div>
        <div className="faq-list">
          {faq.map(([question, answer], index) => <article className={openFaq === index ? "faq-item open" : "faq-item"} key={question}><button aria-expanded={openFaq === index} onClick={() => setOpenFaq(openFaq === index ? null : index)}><span>{question}</span><b aria-hidden="true">{openFaq === index ? "−" : "+"}</b></button>{openFaq === index && <p>{answer}</p>}</article>)}
        </div>
      </section>

      <section className="contact-section" id="contact" aria-labelledby="contact-title">
        <div className="contact-copy">
          <p className="section-kicker light">START THE CONVERSATION</p>
          <h2 id="contact-title">Tell us about your loved one and how we can help.</h2>
          <p>Share a few basic details, and our probate team will review your information and help you understand the next steps.</p>
          <div className="contact-office"><strong>Newark office</strong><address>1058 Mount Vernon Road<br />Newark, Ohio 43055</address><a href="tel:+16143243653">614.324.3653</a><small>Meetings by appointment.</small></div>
        </div>
        {submission === "success" ? (
          <div className="form-card success-card" aria-live="polite"><span aria-hidden="true">✓</span><p className="section-kicker">REQUEST RECEIVED</p><h3>Thank you for reaching out.</h3><p>{formMessage}</p><a href="tel:+18446937837">Need to call? 1-844-My-EQUES</a></div>
        ) : (
          <form className="form-card" onSubmit={submitIntake}>
            <div className="form-heading"><p>PROBATE CONSULTATION REQUEST</p><h3>Begin with the essentials.</h3><span>Required fields are marked with an asterisk.</span></div>
            <div className="form-grid">
              <label><span>Your full name *</span><input name="fullName" autoComplete="name" maxLength={120} required /></label>
              <label><span>Your relationship to the person who died *</span><input name="relationship" maxLength={100} placeholder="e.g., spouse, child, executor" required /></label>
              <label><span>Email *</span><input name="email" type="email" autoComplete="email" maxLength={200} required /></label>
              <label><span>Phone *</span><input name="phone" type="tel" autoComplete="tel" maxLength={50} required /></label>
              <label><span>Preferred contact *</span><select name="preferredContact" defaultValue="" required><option value="" disabled>Select one</option><option>Email</option><option>Phone call</option><option>Text message</option></select></label>
              <label><span>Decedent’s full legal name *</span><input name="decedentName" maxLength={160} required /></label>
              <label><span>Date of death *</span><input name="dateOfDeath" type="date" required /></label>
              <label><span>County of residence at death *</span><input name="county" maxLength={100} placeholder="e.g., Licking County" required /></label>
              <label><span>Original will located? *</span><select name="willStatus" defaultValue="" required><option value="" disabled>Select one</option><option>Yes, original located</option><option>Copy only</option><option>No will known</option><option>Not sure</option></select></label>
              <label><span>Does the estate include real estate? *</span><select name="realEstate" defaultValue="" required><option value="" disabled>Select one</option><option>Yes</option><option>No</option><option>Not sure</option></select></label>
            </div>
            <label className="full-field"><span>Other interested people *</span><input name="interestedParties" maxLength={500} placeholder="Names of spouse, children, beneficiaries, or anyone involved" required /><small>Names help us check for conflicts before discussing the matter.</small></label>
            <label className="full-field"><span>What concerns you most right now? *</span><textarea name="summary" rows={6} minLength={20} maxLength={4000} placeholder="Tell us about property, family concerns, creditor pressure, deadlines, or questions." required /><small>Please do not include Social Security numbers, account numbers, passwords, or upload documents here.</small></label>
            <label className="consent"><input name="consent" type="checkbox" required /><span>I understand that sending this form does not create an attorney-client relationship and does not guarantee that EQUES can represent me. I will not rely on this form for an emergency or deadline. *</span></label>
            <label className="honeypot" aria-hidden="true">Website<input name="website" tabIndex={-1} autoComplete="off" /></label>
            {submission === "error" && <p className="form-error" role="alert">{formMessage} You may also call <a href="tel:+18446937837">1-844-My-EQUES</a>.</p>}
            <div className="submit-row"><button className="button button-gold" type="submit" disabled={submission === "sending"}>{submission === "sending" ? "Sending…" : "Send to the EQUES intake team"} {submission !== "sending" && <Arrow />}</button><p>Sent securely to <strong>contactus@eques.law</strong>. This website does not keep a copy.</p></div>
          </form>
        )}
      </section>

      <footer>
        <div className="brand footer-brand"><strong>EQUES</strong><small>LAW GROUP</small></div>
        <p>Newark-based probate guidance for families across Ohio.</p>
        <p>© 2026 EQUES Law Group · Attorney advertising.</p>
        <small className="disclaimer">This site provides general information, not legal advice. Laws, court forms, deadlines, and local practices may change. No attorney-client relationship is created by visiting this site or submitting the form.</small>
      </footer>
    </main>
  );
}
